import { TagDoc } from "@kwp/contracts";
import { paths, reviveTimestamps } from "@kwp/firestore";
import { useQuery } from "@tanstack/react-query";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { useTRPC } from "../../lib/api";
import { useClients } from "../../lib/clients";
import { useMe } from "../../lib/me";
import { toSavedScope, type SavedScope } from "./scope";

/** The signed-in person's mail accounts (`accounts.list`), shared by the inbox and settings. */
export function useAccounts() {
  const trpc = useTRPC();
  return useQuery(trpc.accounts.list.queryOptions(undefined, { staleTime: 60_000 }));
}

/**
 * Saved scopes, live from `users/{uid}/savedScopes` (written only through `scopes.save/remove`);
 * null until the first snapshot.
 */
export function useSavedScopes(): SavedScope[] | null {
  const { firebase } = useClients();
  const me = useMe();
  const [scopes, setScopes] = useState<SavedScope[] | null>(null);
  useEffect(
    () =>
      onSnapshot(
        query(collection(firebase.db, paths.savedScopes(me.uid)), orderBy("name")),
        (snap) =>
          setScopes(
            snap.docs.flatMap((d) => {
              const s = toSavedScope(d.id, d.data());
              return s === null ? [] : [s];
            }),
          ),
        () => setScopes([]),
      ),
    [firebase.db, me.uid],
  );
  return scopes;
}

/** A tag or folder the sync worker has seen on one of the person's accounts (`accounts/{a}/tags`). */
export interface AccountTag {
  accountId: string;
  name: string;
  kind: "tag" | "folder";
}

/**
 * Tags and folders of the given accounts, live (one listener per account on its `tags`
 * collection, which the rules let only the account's owner read): what the tag and move pickers
 * suggest. Empty until the first snapshots arrive.
 */
export function useAccountTags(accountIds: readonly string[]): AccountTag[] {
  const { firebase } = useClients();
  const key = [...new Set(accountIds)].sort().join(",");
  const [tags, setTags] = useState<Map<string, AccountTag[]>>(() => new Map());
  useEffect(() => {
    if (key === "") return undefined;
    setTags(new Map());
    const unsubscribes = key.split(",").map((accountId) =>
      onSnapshot(
        collection(firebase.db, paths.tags(accountId)),
        (snap) => {
          const list = snap.docs.flatMap((d) => {
            const t = TagDoc.safeParse(reviveTimestamps(d.data()));
            return t.success ? [{ accountId, name: t.data.name, kind: t.data.kind }] : [];
          });
          setTags((all) => new Map(all).set(accountId, list));
        },
        () => setTags((all) => new Map(all).set(accountId, [])),
      ),
    );
    return () => unsubscribes.forEach((u) => u());
  }, [firebase.db, key]);
  return useMemo(() => [...tags.values()].flat(), [tags]);
}
